import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { CSS_RESTRICTIONS } from '@common/constants';
import { EFontStyle } from '@common/enums';
import { type FormArray, FormBuilder } from '@angular/forms';
import { type ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import type { TypographySettingsFormGroup } from '@web/widgets/catalog-products/type';
import { type TypographySettingsFormType } from '@web/widgets/catalog-products/type';
import type { FontStyleFormControl } from '@web/widgets/catalog-products/type/catalog-products-form.type';
import { CommonModule } from '@angular/common';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { ThemeStateService } from '@sites/site-theme/src/state';

enum SettingsType {
    title,
    counter,
    price,
    oldPrice,
}

@Component({
    selector: 'web-typography-settings',
    templateUrl: './typography-settings.component.html',
    styleUrls: ['./typography-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, CommonElementsModule],
})
export class TypographySettingsComponent {
    @Input() typographySettingsFormGroup: TypographySettingsFormGroup;

    protected readonly fb: FormBuilder = inject(FormBuilder);
    private readonly themeState = inject(ThemeStateService);

    public readonly selectedThemeColors = this.themeState.selectedColors;
    public settingsType = SettingsType;
    protected readonly EFontStyle = EFontStyle;

    public setFontSize(settingsType: SettingsType, value: number) {
        switch (settingsType) {
            case SettingsType.title:
                this.titleSettingsFormGroup.get('fontSize').patchValue(value);
                break;
            case SettingsType.counter:
                this.counterSettingsFormGroup.get('fontSize').patchValue(value);
                break;
            case SettingsType.price:
                this.priceSettingsFormGroup.get('fontSize').patchValue(value);
                break;
            case SettingsType.oldPrice:
                this.oldPriceSettingsFormGroup.get('fontSize').patchValue(value);
                break;
        }
    }

    public isActiveTextStyle(settingsType: SettingsType, fontStyle: EFontStyle): boolean {
        switch (settingsType) {
            case SettingsType.title:
                return this.titleSettingsFormGroup.get('fontStyle').value.includes(fontStyle);
            case SettingsType.counter:
                return this.counterSettingsFormGroup.get('fontStyle').value.includes(fontStyle);
            case SettingsType.price:
                return this.priceSettingsFormGroup.get('fontStyle').value.includes(fontStyle);
            case SettingsType.oldPrice:
                return this.oldPriceSettingsFormGroup.get('fontStyle').value.includes(fontStyle);
        }
    }

    public toggleActiveTextStyle(settingsType: SettingsType, fontStyle: EFontStyle): void {
        switch (settingsType) {
            case SettingsType.title:
                this.setFontStyle(
                    this.titleSettingsFormGroup.get('fontStyle') as FormArray<FontStyleFormControl>,
                    fontStyle
                );
                break;
            case SettingsType.counter:
                this.setFontStyle(
                    this.counterSettingsFormGroup.get('fontStyle') as FormArray<FontStyleFormControl>,
                    fontStyle
                );
                break;
            case SettingsType.price:
                this.setFontStyle(
                    this.priceSettingsFormGroup.get('fontStyle') as FormArray<FontStyleFormControl>,
                    fontStyle
                );
                break;
            case SettingsType.oldPrice:
                this.setFontStyle(
                    this.oldPriceSettingsFormGroup.get('fontStyle') as FormArray<FontStyleFormControl>,
                    fontStyle
                );
                break;
        }
    }

    public setTextColor(settingsType: SettingsType, { value }: ColorPickerEventPayload): void {
        switch (settingsType) {
            case SettingsType.title:
                this.titleSettingsFormGroup.get('color').patchValue(value);
                break;
            case SettingsType.counter:
                this.counterSettingsFormGroup.get('color').patchValue(value);
                break;
            case SettingsType.price:
                this.priceSettingsFormGroup.get('color').patchValue(value);
                break;
            case SettingsType.oldPrice:
                this.oldPriceSettingsFormGroup.get('color').patchValue(value);
                break;
        }
    }

    public setIsShowText(settingsType: SettingsType, value: boolean) {
        switch (settingsType) {
            case SettingsType.price:
                this.priceSettingsFormGroup.get('isShow').patchValue(value);
                break;
            case SettingsType.oldPrice:
                this.oldPriceSettingsFormGroup.get('isShow').patchValue(value);
                break;
        }
    }
    public get FONT_SIZE_MIN(): number {
        return CSS_RESTRICTIONS.FONT_SIZE_MIN;
    }

    public get FONT_SIZE_MAX(): number {
        return CSS_RESTRICTIONS.FONT_SIZE_MAX;
    }

    public get titleSettingsFormGroup(): TypographySettingsFormType['titleSettings'] {
        return this.typographySettingsFormGroup.get('titleSettings') as TypographySettingsFormType['titleSettings'];
    }

    public get counterSettingsFormGroup(): TypographySettingsFormType['counterSettings'] {
        return this.typographySettingsFormGroup.get('counterSettings') as TypographySettingsFormType['counterSettings'];
    }

    public get priceSettingsFormGroup(): TypographySettingsFormType['priceSettings'] {
        return this.typographySettingsFormGroup.get('priceSettings') as TypographySettingsFormType['priceSettings'];
    }

    public get oldPriceSettingsFormGroup(): TypographySettingsFormType['oldPriceSettings'] {
        return this.typographySettingsFormGroup.get(
            'oldPriceSettings'
        ) as TypographySettingsFormType['oldPriceSettings'];
    }

    private setFontStyle(control: FormArray<FontStyleFormControl>, fontStyle: EFontStyle) {
        const index = control.value.findIndex((el) => el === fontStyle);

        if (index > -1) {
            control.removeAt(index);
        } else {
            control.push(this.fb.control(fontStyle));
        }
    }
}
